import React from "react";
import SEO from "../../components/g4/SEO";
import { G4Navbar, G4Footer } from "../../components/g4/Chrome";
import ObfuscatedEmail from "../../components/ObfuscatedEmail";

/**
 * Studio G4 Cookie Notice.
 *
 * Mirrors the LRS /cookies structure (essential-cookies statement +
 * "what we do not use" + "your choice" + "if this changes" +
 * "questions"). Studio G4 wording: "This website uses essential
 * cookies to ensure it functions correctly."
 */

function LegalHero({ eyebrow, title, intro }) {
    return (
        <section className="bg-[#0A0A0A] text-white pt-32 pb-14 md:pt-40 md:pb-20">
            <div className="max-w-[720px] mx-auto px-6 md:px-10">
                <p className="font-['Manrope'] font-semibold text-[12px] tracking-[0.22em] uppercase mb-4" style={{ color: "#650505" }}>
                    {eyebrow}
                </p>
                <h1 className="font-['Outfit'] text-[38px] md:text-[52px] font-light leading-[1.05] tracking-tight mb-4">
                    {title}
                </h1>
                {intro && (
                    <p className="font-['Manrope'] text-white/70 text-[16px] md:text-[18px] leading-relaxed">
                        {intro}
                    </p>
                )}
            </div>
        </section>
    );
}

function LegalBody({ children }) {
    return (
        <section className="bg-white py-16 md:py-24">
            <div className="max-w-[720px] mx-auto px-6 md:px-10 font-['Manrope'] text-[17px] leading-[1.75] text-[#0A0A0A]/85 space-y-5">
                {children}
            </div>
        </section>
    );
}

function H2({ children }) {
    return (
        <h2 className="font-['Outfit'] text-[#0A0A0A] text-[24px] md:text-[28px] mt-12 mb-4 leading-tight font-normal">
            {children}
        </h2>
    );
}

function LastUpdated({ date = "17 October 2026" }) {
    return (
        <p className="font-['Manrope'] text-[#0A0A0A]/50 text-[14px] italic">
            Last updated: {date}
        </p>
    );
}

export default function Cookies() {
    return (
        <div data-testid="g4-cookies-page">
            <SEO
                title="Cookie Notice"
                description="How the Studio G4 website uses cookies."
                path="/cookies"
            />
            <G4Navbar />
            <LegalHero
                eyebrow="Cookies"
                title="Cookie Notice"
                intro="How this website uses cookies."
            />
            <LegalBody>
                <LastUpdated />

                <H2>Essential cookies only</H2>
                <p>
                    This website uses essential cookies to ensure it functions correctly, remains secure, and can
                    be administered.
                </p>

                <H2>What we do not use</H2>
                <p>
                    We do not use advertising cookies. We do not use behavioural tracking. We do not use
                    third-party analytics cookies at this time. There is no Meta Pixel or equivalent tracking
                    technology on this site.
                </p>

                <H2>Your choice</H2>
                <p>
                    Because we only use essential cookies required for basic site functionality, we do not
                    require a detailed cookie-consent management interface. The small notice at the bottom of
                    the site confirms that essential cookies are in use; clicking "OK" simply dismisses the
                    notice.
                </p>

                <H2>If this changes</H2>
                <p>
                    If we ever introduce non-essential cookies, we will update this page and provide an
                    appropriate consent mechanism at that time.
                </p>

                <H2>Questions</H2>
                <p>
                    Any questions about how this site uses cookies can be sent to{" "}
                    <ObfuscatedEmail
                        user="info"
                        domain="studio-g4.co.uk"
                        className="underline hover:opacity-70"
                    />
                    .
                </p>
            </LegalBody>
            <G4Footer />
        </div>
    );
}
